import firebase from "firebase/app";
import { v4 as uuidv4 } from "uuid";

const getKeyMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  if (month < 10) {
    return `${year}-0${month}`;
  } else {
    return `${year}-${month}`;
  }
};

const documentRef = (keyMonth) => {
  const db = firebase.firestore();
  const currentUser = firebase.auth().currentUser;
  return db.doc(`transactions/${currentUser.uid}/${keyMonth}/transactions`);
};

const documentTransactionsRef = () => {
  const db = firebase.firestore();
  const currentUser = firebase.auth().currentUser;
  return db.doc(`transactions/${currentUser.uid}`);
};

export default {
  namespaced: true,
  state: {
    transactions: {
      income: [],
      expense: [],
    },
    currentMonth: null,
  },
  mutations: {
    addTransaction(state, { transactionToAdd, type }) {
      // Dont add transaction if adding transaction to different month
      if (
        getKeyMonth(state.currentMonth) !== getKeyMonth(transactionToAdd.date)
      ) {
        return;
      }
      transactionToAdd.date = {
        seconds: transactionToAdd.date.getTime() / 1000,
      };
      state.transactions[type].push(transactionToAdd);
    },
    editTransaction(state, { transactionToEdit, type }) {
      state.transactions[type] = state.transactions[type].map((transaction) => {
        if (transaction.id === transactionToEdit.id) {
          transaction = transactionToEdit;
          transaction.date = {
            seconds: transactionToEdit.date.getTime() / 1000,
          };
        }
        return transaction;
      });
    },
    deleteTransaction(state, { transactionToDelete, type }) {
      state.transactions[type] = state.transactions[type].filter(
        (transaction) => transaction.id !== transactionToDelete.id
      );
    },
    resetTransactions(state) {
      state.transactions = {
        income: [],
        expense: [],
      };
    },
    setTransactions(state, transactions) {
      state.transactions = transactions;
    },
    setCurrentMonth(state, currentMonth) {
      state.currentMonth = currentMonth;
    },
  },
  getters: {
    transactionsIncome(state) {
      return state.transactions.income || [];
    },
    transactionsExpense(state) {
      return state.transactions.expense || [];
    },
    totalIncome(state, getters) {
      if (getters.transactionsIncome.length === 0) {
        return 0;
      }

      return getters.transactionsIncome
        .map((transaction) => transaction.amount)
        .reduce((a, b) => a + b);
    },
    totalExpense(state, getters) {
      if (getters.transactionsExpense.length === 0) {
        return 0;
      }

      return getters.transactionsExpense
        .map((transaction) => transaction.amount)
        .reduce((a, b) => a + b);
    },
    currentMonth(state, currentMonth) {
      return state.currentMonth;
    },
  },
  actions: {
    getMonthTransactions({ commit, state }, date) {
      if (state.currentMonth) {
        const dateA = getKeyMonth(state.currentMonth);
        const dateB = getKeyMonth(date);

        // If no change of dates then dont fetch transations again
        if (dateA === dateB) {
          return new Promise((resolve) => {
            resolve();
          });
        }
      }

      commit("resetTransactions");
      commit("setCurrentMonth", date);

      return new Promise((resolve, reject) => {
        documentRef(getKeyMonth(date))
          .get()
          .then((doc) => {
            if (doc.data()) {
              commit("setTransactions", doc.data());
            }

            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    editTransaction(
      { commit, state },
      { transactionToEdit, originalTransaction }
    ) {
      transactionToEdit.amount = Number(transactionToEdit.amount);

      const type = transactionToEdit.type;
      const switchType = transactionToEdit.type !== originalTransaction.type;
      delete transactionToEdit.type;
      let update;
      if (switchType) {
        update = {
          [type]: [...state.transactions[type]],
          [originalTransaction.type]: [
            ...state.transactions[originalTransaction.type],
          ],
        };

        // move to new type
        update[type].push(transactionToEdit);

        // remove from old type
        update[originalTransaction.type] = update[
          originalTransaction.type
        ].filter((transaction) => transaction.id !== transactionToEdit.id);
      } else {
        update = {
          [type]: [...state.transactions[type]],
        };

        update[type] = update[type].map((transaction) => {
          if (transaction.id === transactionToEdit.id) {
            transaction = transactionToEdit;
          }
          return transaction;
        });
      }

      return new Promise((resolve, reject) => {
        documentRef(getKeyMonth(transactionToEdit.date))
          .update(update)
          .then(() => {
            // TODO Only set when transaction added is in sameMonth
            if (switchType) {
              let transactionChangeDate = update[type].find(
                (transaction) => transaction.id === transactionToEdit.id
              );
              transactionChangeDate.date = transactionChangeDate.date = {
                seconds: transactionChangeDate.date.getTime() / 1000,
              };

              commit("setTransactions", update);
            } else {
              commit("editTransaction", {
                transactionToEdit,
                type,
              });
            }

            resolve();
          })
          .catch((error) => reject(error));
      });
    },
    addTransaction({ commit, state }, transactionToAdd) {
      transactionToAdd.amount = Number(transactionToAdd.amount);
      transactionToAdd.id = uuidv4();

      const type = transactionToAdd.type;
      delete transactionToAdd.type;
      let update = {};

      return new Promise((resolve, reject) => {
        return firebase
          .firestore()
          .runTransaction((transaction) => {
            // Gets document and if not exist create
            // If exists update
            return transaction
              .get(documentRef(getKeyMonth(transactionToAdd.date)))
              .then((doc) => {
                if (doc.data()) {
                  update = doc.data();
                } else {
                  update = { income: [], expense: [] };
                }

                update[type] = [...update[type], transactionToAdd];

                if (!doc.exists) {
                  const currentUser = firebase.auth().currentUser;
                  const db = firebase.firestore();
                  documentTransactionsRef()
                    .update({
                      transactions: firebase.firestore.FieldValue.arrayUnion(
                        `transactions/${currentUser.uid}/${getKeyMonth(
                          transactionToAdd.date
                        )}/transactions`
                      ),
                    })
                    .then(
                      () => {},
                      () => {
                        documentTransactionsRef().set({
                          transactions: [
                            `transactions/${currentUser.uid}/${getKeyMonth(
                              transactionToAdd.date
                            )}/transactions`,
                          ],
                        });
                      }
                    );

                  documentRef(getKeyMonth(transactionToAdd.date)).set(update);
                } else {
                  transaction.update(
                    documentRef(getKeyMonth(transactionToAdd.date)),
                    update
                  );
                }
              });
          })
          .then(() => {
            commit("addTransaction", {
              transactionToAdd,
              type,
            });
            resolve();
          })
          .catch((error) => reject(error));
      });
    },
    deleteTransaction({ commit, state }, transactionToDelete) {
      const type = transactionToDelete.type;
      delete transactionToDelete.type;

      let update = {
        [type]: [...state.transactions[type]],
      };
      update[type] = update[type].filter(
        (transaction) => transaction.id !== transactionToDelete.id
      );

      return new Promise((resolve, reject) => {
        documentRef(getKeyMonth(transactionToDelete.date))
          .update(update)
          .then(() => {
            commit("deleteTransaction", {
              transactionToDelete,
              type,
            });
            resolve();
          })
          .catch((error) => reject(error));
      });
    },
  },
};
